import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { toast } from "sonner";

const SERVICE_TYPES = [
  { value: "service", label: "Service" },
  { value: "package", label: "Package" },
  { value: "surgery", label: "Surgery" },
  { value: "ot", label: "OT charge" },
  { value: "lab", label: "Lab" },
  { value: "pharmacy", label: "Pharmacy" },
  { value: "implant", label: "Implant" },
  { value: "daycare", label: "Day-care / Bed" },
  { value: "other", label: "Other" },
];

const EMPTY_BI = { code: "", name: "", type: "service", rate: 0, gst_percent: 0, description: "", active: true };

/**
 * Billing Items master — full CRUD against /api/billing/service-masters.
 * This is the EXACT same dataset the IPD bill picker reads from.
 */
function BillingItemsMaster() {
  const [items, setItems] = useState([]);
  const [filterType, setFilterType] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);   // existing id when editing
  const [form, setForm] = useState(EMPTY_BI);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/billing/service-masters");
    setItems(data.items || []);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let xs = items;
    if (filterType) xs = xs.filter(x => x.type === filterType);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      xs = xs.filter(x => (x.name || "").toLowerCase().includes(needle) || (x.code || "").toLowerCase().includes(needle));
    }
    return xs;
  }, [items, filterType, q]);

  const startAdd = () => { setEditing(null); setForm(EMPTY_BI); setOpen(true); };
  const startEdit = (it) => {
    setEditing(it.id);
    setForm({
      code: it.code || "", name: it.name || "", type: it.type || "service",
      rate: it.rate || 0, gst_percent: it.gst_percent || 0,
      description: it.description || "", active: it.active !== false,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.code.trim()) { toast.error("Item code is required"); return; }
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        name: form.name.trim(),
        type: form.type,
        rate: parseFloat(form.rate) || 0,
        gst_percent: parseFloat(form.gst_percent) || 0,
        description: form.description || "",
        active: !!form.active,
        package_items: [],
      };
      if (editing) {
        await api.patch(`/billing/service-masters/${editing}`, payload);
        toast.success("Updated");
      } else {
        await api.post(`/billing/service-masters`, payload);
        toast.success("Added");
      }
      setOpen(false);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.name}"?\nThis cannot be undone.`)) return;
    try {
      await api.delete(`/billing/service-masters/${it.id}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <Card className="p-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name or code…" className="pl-9" data-testid="bi-search" />
          </div>
          <div className="w-44">
            <Select value={filterType || "_all"} onValueChange={v => setFilterType(v === "_all" ? "" : v)}>
              <SelectTrigger data-testid="bi-filter-type"><SelectValue placeholder="All types" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">All types</SelectItem>
                {SERVICE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={startAdd} className="bg-brand hover:bg-brand-700" data-testid="bi-add-btn">
            <Plus size={14} className="mr-2" />Add billing item
          </Button>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="border-b border-border bg-secondary/50 px-3 py-2 text-[11px] text-ink-muted">
          {filtered.length} of {items.length} items — this is the master list used by the IPD bill picker.
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-xs uppercase tracking-wider text-ink-muted">
              <tr>
                <th className="px-3 py-2 text-left">Code</th>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-right">Rate</th>
                <th className="px-3 py-2 text-right">GST %</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-10 text-center text-ink-muted">No items match.</td></tr>
              )}
              {filtered.map(it => (
                <tr key={it.id} data-testid={`bi-row-${it.code}`}>
                  <td className="px-3 py-2 font-mono text-xs">{it.code}</td>
                  <td className="px-3 py-2"><div className="font-medium">{it.name}</div>{it.description && <div className="text-[10px] text-ink-muted">{it.description}</div>}</td>
                  <td className="px-3 py-2 text-xs capitalize">{it.type}</td>
                  <td className="px-3 py-2 text-right">₹ {Number(it.rate || 0).toLocaleString("en-IN")}</td>
                  <td className="px-3 py-2 text-right">{it.gst_percent || 0}%</td>
                  <td className="px-3 py-2">
                    {it.active === false
                      ? <Badge variant="outline" className="border-rose-300 bg-rose-50 text-rose-700">Inactive</Badge>
                      : <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-700">Active</Badge>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => startEdit(it)} data-testid={`bi-edit-${it.code}`}>
                      <Pencil size={11} />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(it)} className="text-rose-700 hover:bg-rose-50" data-testid={`bi-delete-${it.code}`}>
                      <Trash2 size={11} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Add / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit billing item" : "Add billing item"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>Item code *</Label>
              <Input value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} placeholder="e.g. PKG-PHACO-FOLD" disabled={!!editing} data-testid="bi-form-code" />
              {editing && <div className="mt-1 text-[10px] text-ink-muted">Code cannot be changed after creation.</div>}
            </div>
            <div>
              <Label>Type *</Label>
              <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="bi-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>{SERVICE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label>Name *</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Phacoemulsification + Foldable IOL" data-testid="bi-form-name" />
            </div>
            <div>
              <Label>Rate (₹) *</Label>
              <Input type="number" value={form.rate} onChange={e => setForm({ ...form, rate: e.target.value })} data-testid="bi-form-rate" />
            </div>
            <div>
              <Label>GST %</Label>
              <Input type="number" value={form.gst_percent} onChange={e => setForm({ ...form, gst_percent: e.target.value })} data-testid="bi-form-gst" />
            </div>
            <div className="sm:col-span-2">
              <Label>Description</Label>
              <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Optional notes shown under the item on the picker" data-testid="bi-form-desc" />
            </div>
            <div className="sm:col-span-2 flex items-center gap-2">
              <input type="checkbox" id="bi-active" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} data-testid="bi-form-active" />
              <label htmlFor="bi-active" className="text-sm">Active — show in bill picker</label>
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <Button onClick={save} disabled={busy} className="bg-brand hover:bg-brand-700" data-testid="bi-form-save">
              {editing ? "Update" : "Add"} item
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Generic add+list section for the legacy /masters/* endpoints (medicines, investigations).
 * Kept as-is to avoid touching unrelated flows.
 */
function Section({ title, columns, fetchUrl, postUrl, fields, formInit }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(formInit);

  const load = async () => { const { data } = await api.get(fetchUrl); setItems(data.items); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const set = (k, v) => setForm(s => ({ ...s, [k]: v }));
  const add = async () => {
    try {
      await api.post(postUrl, form); toast.success(`${title.slice(0,-1)} added`);
      setForm(formInit); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="grid gap-3 md:grid-cols-5">
          {fields.map(f => (
            <div key={f.key} className={f.span ? `md:col-span-${f.span}` : ""}>
              <Label>{f.label}</Label>
              <Input type={f.type || "text"} value={form[f.key]} onChange={e => set(f.key, f.type === "number" ? Number(e.target.value) : e.target.value)} />
            </div>
          ))}
          <Button onClick={add} className="bg-brand hover:bg-brand-700 md:self-end" data-testid={`master-add-${title.toLowerCase()}`}><Plus size={14} className="mr-2" />Add</Button>
        </div>
      </Card>
      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-xs uppercase tracking-wider text-ink-muted">
            <tr>{columns.map(c => <th key={c.key} className="px-4 py-2 text-left">{c.label}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.length === 0 && <tr><td colSpan={columns.length} className="px-4 py-8 text-center text-ink-muted">No entries.</td></tr>}
            {items.map((i, idx) => (
              <tr key={i.id || idx}>{columns.map(c => <td key={c.key} className="px-4 py-2">{c.format ? c.format(i[c.key]) : (i[c.key] ?? "—")}</td>)}</tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

export default function AdminMasters() {
  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <div className="section-label">Master settings</div>
        <h1 className="font-heading text-3xl font-semibold">Masters</h1>
        <p className="mt-1 text-sm text-ink-muted">Billing items, medicines, investigations and other reference data used across the app.</p>
      </div>

      <Tabs defaultValue="billing-items">
        <TabsList>
          <TabsTrigger value="billing-items" data-testid="masters-tab-billing-items">Billing items</TabsTrigger>
          <TabsTrigger value="medicines" data-testid="masters-tab-medicines">Medicines</TabsTrigger>
          <TabsTrigger value="investigations" data-testid="masters-tab-investigations">Investigations</TabsTrigger>
        </TabsList>

        <TabsContent value="billing-items" className="mt-4">
          <BillingItemsMaster />
        </TabsContent>

        <TabsContent value="medicines" className="mt-4">
          <Section title="Medicines" fetchUrl="/masters/medicines" postUrl="/masters/medicines"
            fields={[
              { key: "name", label: "Name", span: 2 },
              { key: "generic", label: "Generic" },
              { key: "form", label: "Form" },
              { key: "mrp", label: "MRP (₹)", type: "number" },
            ]}
            formInit={{ name: "", generic: "", form: "eye_drop", mrp: 0, gst_percent: 12 }}
            columns={[
              { key: "name", label: "Name" }, { key: "generic", label: "Generic" }, { key: "form", label: "Form" },
              { key: "mrp", label: "MRP", format: v => `₹ ${v}` }, { key: "stock", label: "Stock" },
            ]}
          />
        </TabsContent>

        <TabsContent value="investigations" className="mt-4">
          <Section title="Investigations" fetchUrl="/masters/investigations" postUrl="/masters/investigations"
            fields={[
              { key: "name", label: "Name", span: 2 },
              { key: "category", label: "Category" },
              { key: "price", label: "Price (₹)", type: "number" },
              { key: "normal_range", label: "Normal range" },
            ]}
            formInit={{ name: "", category: "lab", price: 0, normal_range: "", unit: "" }}
            columns={[
              { key: "name", label: "Name" }, { key: "category", label: "Category" },
              { key: "price", label: "Price", format: v => `₹ ${v}` }, { key: "normal_range", label: "Normal range" },
            ]}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
